import React, { useEffect, useMemo, useState } from 'react';

import {
    Alert,
    Button,
    Card,
    Col,
    Divider,
    InputNumber,
    Modal,
    Grid,
    Row,
    Space,
    Statistic,
    Typography,
    message
} from 'antd';

import {
    CheckCircleOutlined,
    ClearOutlined,
    ReloadOutlined,
    ShoppingCartOutlined
} from '@ant-design/icons';

import KermesQuantityCard from './KermesQuantityCard';

import {
    calculateKermesTotal,
    calculateKermesChange
} from '../utils/kermesPricing';

import {
    createKermesOrder,
    getKermesInventory
} from '../utils/api';

const { Text } = Typography;


// Los dos productos que participan
// en la promoción de la Kermés.
const PROMO_PRODUCT_IDS = [1, 2];


export default function KermesCaja({
    apiBase = '/api/kermes',
    userId = null,
    onSaleRegistered
}) {

    const [products, setProducts] = useState([]);

    const [quantities, setQuantities] = useState({});

    const [payment, setPayment] = useState(0);

    const [loading, setLoading] = useState(false);

    const [loadingProducts, setLoadingProducts] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;


    // =========================
    // CARGAR PRODUCTOS
    // =========================

    const loadProducts = async () => {

        setLoadingProducts(true);

        try {

            const data = await getKermesInventory();

            setProducts(data.inventory || []);

        } catch (error) {

            message.error(
                error.message ||
                'No se pudieron cargar los productos.'
            );

        } finally {

            setLoadingProducts(false);

        }
    };


    useEffect(() => {

        loadProducts();

    }, []);


    // =========================
    // PRODUCTOS CON CANTIDAD
    // =========================

    const selectedProducts = useMemo(() => {

        return products
            .map(product => ({
                ...product,
                quantity: Number(
                    quantities[product.product_id] || 0
                )
            }))
            .filter(product => product.quantity > 0);

    }, [products, quantities]);


    // =========================
    // PANUCHOS + SALBUTES
    // =========================

    const promoItems = useMemo(() => {

        return selectedProducts
            .filter(product =>
                PROMO_PRODUCT_IDS.includes(
                    Number(product.product_id)
                )
            );

    }, [selectedProducts]);


    const promoQuantity = useMemo(() => {

        return promoItems.reduce(
            (sum, product) =>
                sum + product.quantity,
            0
        );

    }, [promoItems]);


    // =========================
    // OTROS PRODUCTOS
    // =========================

    const otherProducts = useMemo(() => {

        return selectedProducts.filter(product =>
            !PROMO_PRODUCT_IDS.includes(
                Number(product.product_id)
            )
        );

    }, [selectedProducts]);


    // =========================
    // TOTAL
    // =========================

    const total = useMemo(() => {

        // Total de Panuchos + Salbutes
        const promoTotal =
            calculateKermesTotal(promoQuantity);

        // Productos adicionales
        const otherTotal =
            otherProducts.reduce(
                (sum, product) => {

                    const price =
                        Number(product.price || 0);

                    return sum +
                        (price * product.quantity);

                },
                0
            );

        return Number(
            (promoTotal + otherTotal).toFixed(2)
        );

    }, [
        promoQuantity,
        otherProducts
    ]);


    // =========================
    // TOTAL DE PIEZAS
    // =========================

    const totalItems = useMemo(() => {

        return selectedProducts.reduce(
            (sum, product) =>
                sum + product.quantity,
            0
        );

    }, [selectedProducts]);


    // =========================
    // CAMBIO
    // =========================

    const change = useMemo(
        () => calculateKermesChange(
            total,
            payment
        ),
        [total, payment]
    );


    // =========================
    // CAMBIAR CANTIDAD
    // =========================

    const handleQuantityChange = (
        productId,
        value
    ) => {

        setQuantities(prev => ({
            ...prev,
            [productId]: Number(value || 0)
        }));

    };


    // =========================
    // LIMPIAR VENTA
    // =========================

    const clearSale = () => {

        setQuantities({});

        setPayment(0);

    };


    // =========================
    // SOLICITAR REGISTRO
    // =========================

    const requestRegister = () => {

        if (totalItems <= 0) {

            message.warning(
                'Agrega al menos una pieza.'
            );

            return;
        }

        const paymentCents =
            Math.round(
                Number(payment || 0) * 100
            );

        const totalCents =
            Math.round(
                Number(total || 0) * 100
            );

        if (paymentCents < totalCents) {

            message.error(
                'El efectivo recibido es insuficiente.'
            );

            return;
        }

        setConfirmOpen(true);
    };


    // =========================
    // REGISTRAR VENTA
    // =========================

    const registerSale = async () => {

        setLoading(true);

        try {

            const items = selectedProducts.map(
                product => ({
                    product_id:
                        Number(product.product_id),

                    quantity:
                        Number(product.quantity)
                })
            );

            const data =
                await createKermesOrder({
                    user_id: userId,
                    payment,
                    items
                });

            if (!data.success) {

                throw new Error(
                    data.message ||
                    'No se pudo registrar la venta.'
                );
            }

            message.success(
                `Venta #${data.order_id} registrada correctamente.`
            );

            setConfirmOpen(false);

            clearSale();

            // Actualizar existencias
            await loadProducts();

            if (onSaleRegistered) {

                onSaleRegistered(data);

            }

        } catch (error) {

            message.error(
                error.message
            );

        } finally {

            setLoading(false);

        }
    };


    // =========================
    // ICONO DEL PRODUCTO
    // =========================

    const getProductIcon = product => {

        const id =
            Number(product.product_id);

        if (id === 1) {
            return '🫓';
        }

        if (id === 2) {
            return '🥟';
        }

        return '🍽️';
    };


    return (
        <>
            <Card
                title={
                    <Space>
                        <ShoppingCartOutlined />
                        <span>Caja - Kermés</span>
                    </Space>
                }

                loading={loadingProducts}

                extra={
                    <Space size={8}>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={loadProducts}
                            loading={loadingProducts}
                            title="Actualizar"
                        >
                            {!isMobile && 'Actualizar'}
                        </Button>

                        <Button
                            icon={<ClearOutlined />}
                            onClick={clearSale}
                            disabled={loading}
                            title="Limpiar"
                        >
                            {!isMobile && 'Limpiar'}
                        </Button>
                    </Space>
                }
            >

                {/* =========================
                PRODUCTOS
            ========================= */}

                <Row gutter={[16, 16]}>
                    {products.map(product => (
                        <Col
                            xs={24}
                            md={12}
                            key={product.product_id}
                        >
                            <KermesQuantityCard
                                icon={getProductIcon(product)}
                                title={product.name}
                                value={
                                    quantities[
                                    product.product_id
                                    ] || 0
                                }
                                onChange={value =>
                                    handleQuantityChange(
                                        product.product_id,
                                        value
                                    )
                                }
                                disabled={loading}
                            />

                            <Text
                                type="secondary"
                                style={{
                                    display: 'block',
                                    marginTop: 4
                                }}
                            >
                                ${Number(
                                    product.price || 0
                                ).toFixed(2)} c/u
                            </Text>
                        </Col>
                    ))}
                </Row>


                <Divider />


                {/* =========================
                RESUMEN
            ========================= */}

                <Row gutter={[16, 16]}>

                    <Col xs={12} sm={8}>
                        <Statistic
                            title="Piezas"
                            value={totalItems}
                        />
                    </Col>

                    <Col xs={12} sm={8}>
                        <Statistic
                            title="Total"
                            prefix="$"
                            value={total}
                            precision={2}
                        />
                    </Col>

                    <Col xs={24} sm={8}>

                        <Text strong>
                            Efectivo recibido
                        </Text>

                        <InputNumber
                            prefix="$"
                            min={0}
                            value={payment}
                            precision={2}
                            controls={false}
                            size="large"

                            onChange={value =>
                                setPayment(
                                    Number(value || 0)
                                )
                            }

                            style={{
                                width: '100%',
                                marginTop: 8
                            }}
                        />


                        {/* =========================
                        DENOMINACIONES
                    ========================= */}

                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns:
                                    'repeat(auto-fit, minmax(70px, 1fr))',
                                gap: 8,
                                marginTop: 10,
                                width: '100%'
                            }}
                        >
                            {[
                                15,
                                20,
                                25,
                                50,
                                100,
                                200,
                                500,
                                1000
                            ].map(amount => (

                                <Button
                                    key={amount}
                                    size="large"

                                    onClick={() =>
                                        setPayment(amount)
                                    }

                                    style={{
                                        width: '100%',
                                        minWidth: 0
                                    }}
                                >
                                    ${amount}
                                </Button>

                            ))}
                        </div>

                    </Col>

                </Row>


                <Divider />


                {/* =========================
                FALTANTE
            ========================= */}

                {payment > 0 &&
                    payment < total && (

                        <Alert
                            type="warning"
                            showIcon

                            message={
                                `Faltan $${(
                                    total - payment
                                ).toFixed(2)}`
                            }

                            style={{
                                marginBottom: 16
                            }}
                        />

                    )}


                {/* =========================
                CAMBIO
            ========================= */}

                <Card size="small">

                    <Statistic
                        title="Cambio"
                        prefix="$"
                        value={change}
                        precision={2}

                        valueStyle={{
                            fontSize: 32
                        }}
                    />

                </Card>


                {/* =========================
                REGISTRAR
            ========================= */}

                <Button
                    type="primary"
                    size="large"
                    block

                    icon={
                        <CheckCircleOutlined />
                    }

                    disabled={
                        totalItems <= 0 ||
                        loading
                    }

                    loading={loading}

                    onClick={
                        requestRegister
                    }

                    style={{
                        height: 58,
                        marginTop: 16,
                        fontSize: 20
                    }}
                >
                    Registrar venta
                </Button>

            </Card>


            {/* =========================
            CONFIRMAR VENTA
        ========================= */}

            <Modal
                title="Confirmar venta"

                open={confirmOpen}

                onCancel={() =>
                    setConfirmOpen(false)
                }

                confirmLoading={loading}

                onOk={
                    registerSale
                }

                okText="Confirmar venta"

                cancelText="Cancelar"
            >

                <Space
                    orientation="vertical"
                    size={12}
                    style={{
                        width: '100%'
                    }}
                >

                    {selectedProducts.map(
                        product => (

                            <Text
                                key={
                                    product.product_id
                                }
                            >
                                {getProductIcon(
                                    product
                                )}{' '}

                                {product.name}:{' '}

                                <strong>
                                    {product.quantity}
                                </strong>

                                {' × '}

                                ${Number(
                                    product.price || 0
                                ).toFixed(2)}
                            </Text>

                        )
                    )}


                    <Divider
                        style={{
                            margin: '8px 0'
                        }}
                    />


                    <Statistic
                        title="Total"
                        prefix="$"
                        value={total}
                        precision={2}
                    />


                    <Statistic
                        title="Efectivo"
                        prefix="$"
                        value={payment}
                        precision={2}
                    />


                    <Statistic
                        title="Cambio"
                        prefix="$"
                        value={change}
                        precision={2}
                    />

                </Space>

            </Modal>

        </>
    );
}